/**
 * PlanCard Component
 * 
 * Card de plano de assinatura
 * - Exibe features do plano
 * - Destaque visual (badge, gradient)
 * - CTA button
 * 
 * Fase 5 - UI de Pagamentos
 */

import { Check, Star, Zap, Crown } from 'lucide-react';

interface PlanCardProps {
  plan: 'free' | 'basic' | 'premium';
  price: number; // Em centavos
  billingCycle: 'monthly' | 'semester' | 'annual';
  features: string[];
  isCurrentPlan: boolean;
  isRecommended?: boolean;
  onSelect: () => void;
}

export function PlanCard({
  plan,
  price,
  billingCycle,
  features,
  isCurrentPlan,
  isRecommended = false,
  onSelect
}: PlanCardProps) {
  // Configuração de cada plano
  const planConfig = {
    free: {
      icon: Star,
      color: 'gray',
      badge: null,
      buttonColor: 'bg-gray-800 hover:bg-gray-900'
    },
    basic: {
      icon: Zap,
      color: 'blue',
      badge: 'Mais Popular',
      buttonColor: 'bg-blue-600 hover:bg-blue-700'
    },
    premium: {
      icon: Crown,
      color: 'purple',
      badge: 'Recomendado',
      buttonColor: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
    }
  };

  const config = planConfig[plan];
  const Icon = config.icon;

  // Calcular economia anual
  const savingsPercent = billingCycle === 'annual' ? 17 : billingCycle === 'semester' ? 9 : 0;

  return (
    <div
      className={`
        relative p-6 rounded-2xl border-2 transition-all
        ${isCurrentPlan ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:shadow-lg'}
        ${plan === 'premium' ? 'bg-gradient-to-br from-purple-50 to-pink-50' : 'bg-white'}
      `}
    >
      {/* Badge de Destaque */}
      {config.badge && !isCurrentPlan && (isRecommended || plan === 'basic' || plan === 'premium') && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
            {config.badge}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-4">
        <Icon className={`w-12 h-12 mx-auto mb-2 text-${config.color}-600`} />
        <h3 className="text-2xl font-bold capitalize">{plan === 'free' ? 'Gratuito' : plan === 'basic' ? 'Básico' : 'Premium'}</h3>
      </div>

      {/* Preço */}
      <div className="text-center mb-6">
        {price === 0 ? (
          <div>
            <p className="text-4xl font-bold">R$ 0,00</p>
            <p className="text-sm text-gray-600">para sempre</p>
          </div>
        ) : (
          <div>
            <p className="text-4xl font-bold">
              R$ {(price / 100).toFixed(2)}
            </p>
            <p className="text-sm text-gray-600">
              {billingCycle === 'monthly' && '/mês'}
              {billingCycle === 'semester' && '/semestre'}
              {billingCycle === 'annual' && '/ano'}
            </p>
            {savingsPercent > 0 && (
              <p className="text-xs text-green-600 font-semibold mt-1">
                Economize {savingsPercent}%
              </p>
            )}
          </div>
        )}
      </div>

      {/* Features */}
      <ul className="space-y-3 mb-6 min-h-[200px]">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2 text-sm">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <span className="text-gray-700">{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA Button */}
      <button
        onClick={onSelect}
        disabled={isCurrentPlan}
        className={`
          w-full py-3 rounded-xl font-semibold transition-all text-white
          ${isCurrentPlan 
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
            : config.buttonColor
          }
        `}
      >
        {isCurrentPlan ? 'Plano Atual' : plan === 'free' ? 'Escolher Gratuito' : 'Assinar Agora'}
      </button>
    </div>
  );
}
